import { ArrowLeft, CheckCircle2, FileSpreadsheet, Upload, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsPtba, useVersionPtba, useVersionsPtba } from '@/api/hooks';
import { LIBELLE_CHANGEMENT_PTBA, LIBELLE_STATUT_VERSION, type ChangementPtba, type DetailVersionPtba, type RapportPtba, type StatutVersionPtba, type VersionPtba } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ConfirmationDialog, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, dateHeureFr, montant, nombre } from '@/lib/utils';

const PROFILS_IMPORT = ['DIRECTEUR_FINANCIER', 'CONTROLE_GESTION', 'ADMINISTRATEUR'];
const PROFILS_LECTURE = [...PROFILS_IMPORT, 'DIRECTEUR_GENERAL', 'DGA_FINANCES', 'DGA_OPERATIONS'];
const VARIANTE_STATUT: Record<StatutVersionPtba, 'default' | 'secondary' | 'destructive' | 'outline'> = { BROUILLON: 'outline', SOUMIS: 'secondary', APPROUVE: 'default', REMPLACE: 'outline', REJETE: 'destructive' };
const COULEUR_CHANGEMENT: Record<ChangementPtba['nature'], string> = { NOUVELLE: 'text-emerald-700', MODIFIEE: 'text-sky-700', INCHANGEE: 'text-muted-foreground', ABSENTE: 'text-red-700', RENOMMEE: 'text-sky-700', REAFFECTEE: 'text-amber-700', REDUITE: 'text-red-700' };
const TYPE_CHANGEMENT: Record<ChangementPtba['type'], string> = { SOUS_ACTIVITE: 'Sous-activité', LIGNE: 'Ligne de dépense', PROVISION: 'Provision' };

export const StatutVersionBadge = ({ statut }: { statut: StatutVersionPtba }) => <Badge variant={VARIANTE_STATUT[statut]}>{LIBELLE_STATUT_VERSION[statut]}</Badge>;

function Anomalies({ liste, classe }: { liste: RapportPtba['erreurs']; classe: string }) {
  return (
    <ul className={cn('max-h-48 space-y-0.5 overflow-y-auto rounded-md border p-2 text-xs', classe)}>
      {liste.slice(0, 60).map((a, k) => (
        <li key={k}>
          {a.ligne ? `Ligne ${a.ligne}` : 'Export'}
          {a.code ? ` · ${a.code}` : ''} : {a.message}
        </li>
      ))}
      {liste.length > 60 && <li>… et {liste.length - 60} autre(s).</li>}
    </ul>
  );
}

/** Compte rendu d'un import ou d'une version : contrôles, totaux par entité, changements par identifiant. */
export function RapportPtbaVue({ rapport, compact = false }: { rapport: RapportPtba; compact?: boolean }) {
  const [filtre, setFiltre] = useState<'TOUS' | 'SOUS_ACTIVITE' | 'PROVISION' | 'LIGNE'>('SOUS_ACTIVITE');
  const [masquerInchangees, setMasquerInchangees] = useState(true);
  const changements = rapport.changements.filter((c) => (filtre === 'TOUS' || c.type === filtre) && (!masquerInchangees || c.nature !== 'INCHANGEE'));
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        <div>
          <div className="text-xs text-muted-foreground">Version du classeur</div>
          <div className="font-medium">
            {rapport.version.numero ?? '—'} <span className="font-normal text-muted-foreground">· {rapport.version.statut ?? '—'}</span>
          </div>
          {(rapport.version.date || rapport.version.auteur) && <div className="text-xs text-muted-foreground">{[rapport.version.date ? dateFr(rapport.version.date) : null, rapport.version.auteur].filter(Boolean).join(' · ')}</div>}
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Lignes de l’export</div>
          <div className="num font-medium">
            {rapport.lignes.conformes} dépense(s) · {rapport.lignes.provisions} provision(s)
          </div>
          <div className="text-xs text-muted-foreground">{rapport.lignes.lues} lue(s), {rapport.lignes.totaux} total(aux) de contrôle</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Sous-activités</div>
          <div className="num font-medium">
            {rapport.sousActivites.nouvelles} nouvelle(s) · {rapport.sousActivites.modifiees + rapport.sousActivites.renommees + rapport.sousActivites.reaffectees} modifiée(s) · {rapport.sousActivites.inchangees} inchangée(s)
          </div>
          {rapport.sousActivites.absentes > 0 && <div className="text-xs text-red-700">{rapport.sousActivites.absentes} absente(s), à désactiver</div>}
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Budget de la version</div>
          <div className="num font-medium">{montant(rapport.budget.version)}</div>
          <div className="text-xs text-muted-foreground">{rapport.budget.approuve === null ? 'aucune version approuvée en service' : `version approuvée ${montant(rapport.budget.approuve)} · écart ${rapport.budget.ecart !== null && rapport.budget.ecart > 0 ? '+' : ''}${nombre(rapport.budget.ecart)} USD`}</div>
        </div>
      </div>
      {rapport.erreurs.length > 0 && <Anomalies liste={rapport.erreurs} classe="border-red-200 bg-red-50 text-red-800" />}
      {rapport.avertissements.length > 0 && <Anomalies liste={rapport.avertissements} classe="border-amber-200 bg-amber-50 text-amber-800" />}
      {rapport.totauxEntites.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Totaux par entité</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entité</TableHead>
                <TableHead>Structure</TableHead>
                <TableHead className="text-right">Lignes</TableHead>
                <TableHead className="text-right">Budget</TableHead>
                <TableHead className="text-right">Provision</TableHead>
                <TableHead className="text-right">Total de la fiche</TableHead>
                <TableHead className="text-right">Écart</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rapport.totauxEntites.map((t) => (
                <TableRow key={t.entite}>
                  <TableCell className="font-medium">{t.entite}</TableCell>
                  <TableCell>{t.structure ?? '—'}</TableCell>
                  <TableCell className="num text-right">{t.lignes}</TableCell>
                  <TableCell className="num text-right">{nombre(t.budget)}</TableCell>
                  <TableCell className="num text-right">{nombre(t.provision)}</TableCell>
                  <TableCell className="num text-right">{t.totalFiche === null ? '—' : nombre(t.totalFiche)}</TableCell>
                  <TableCell className={cn('num text-right', t.ecart !== null && Math.abs(t.ecart) > 1 && 'font-medium text-amber-700')} title={t.ecart !== null && Math.abs(t.ecart) > 1 ? 'Lignes en attente de correction dans le classeur' : undefined}>
                    {t.ecart === null ? '—' : nombre(t.ecart)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {!compact && rapport.changements.length > 0 && (
        <div>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Changements par identifiant</div>
            <div className="flex flex-wrap items-center gap-1">
              {(['SOUS_ACTIVITE', 'PROVISION', 'LIGNE', 'TOUS'] as const).map((f) => (
                <Button key={f} type="button" size="sm" variant={filtre === f ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setFiltre(f)}>
                  {f === 'TOUS' ? 'Tout' : f === 'SOUS_ACTIVITE' ? 'Sous-activités' : f === 'PROVISION' ? 'Provisions' : 'Lignes de dépense'}
                </Button>
              ))}
              <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setMasquerInchangees((v) => !v)}>
                {masquerInchangees ? 'Afficher les inchangées' : 'Masquer les inchangées'}
              </Button>
            </div>
          </div>
          {!changements.length ? (
            <Vide message="Aucun changement pour ce filtre." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Identifiant</TableHead>
                  <TableHead>Intitulé</TableHead>
                  <TableHead>Changement</TableHead>
                  <TableHead className="text-right">Avant</TableHead>
                  <TableHead className="text-right">Après</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {changements.slice(0, 400).map((c, k) => (
                  <TableRow key={`${c.type}-${c.code}-${k}`}>
                    <TableCell className="whitespace-nowrap font-mono text-xs">
                      {c.code}
                      <div className="font-sans text-[11px] text-muted-foreground">{TYPE_CHANGEMENT[c.type]}</div>
                    </TableCell>
                    <TableCell>
                      {c.libelle}
                      {c.detail && <div className="text-xs text-muted-foreground">{c.detail}</div>}
                    </TableCell>
                    <TableCell className={cn('whitespace-nowrap font-medium', COULEUR_CHANGEMENT[c.nature])}>{LIBELLE_CHANGEMENT_PTBA[c.nature]}</TableCell>
                    <TableCell className="num text-right">{c.budgetAvant === undefined ? '—' : nombre(c.budgetAvant)}</TableCell>
                    <TableCell className="num text-right">{c.budgetApres === undefined ? '—' : nombre(c.budgetApres)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {changements.length > 400 && <div className="mt-1 text-xs text-muted-foreground">… et {changements.length - 400} autre(s).</div>}
        </div>
      )}
    </div>
  );
}

function DialogueImportPtba({ ouvert, onOpenChange, exercice: initial }: { ouvert: boolean; onOpenChange: (o: boolean) => void; exercice: number }) {
  const actions = useActionsPtba();
  const [exercice, setExercice] = useState(initial);
  const [mode, setMode] = useState<'fichier' | 'lien'>('fichier');
  const [fichier, setFichier] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [rapport, setRapport] = useState<RapportPtba | null>(null);
  const enCours = actions.importerFichier.isPending || actions.importerLien.isPending;
  const pret = mode === 'fichier' ? Boolean(fichier) : /^https:\/\/\S+$/.test(url.trim());
  const peutEnregistrer = Boolean(rapport && rapport.simulation && !rapport.erreurs.length && rapport.exercice === exercice);
  const lancer = async (simulation: boolean) => {
    try {
      const r = mode === 'fichier' ? await actions.importerFichier.mutateAsync({ exercice, fichier: fichier!, simulation }) : await actions.importerLien.mutateAsync({ exercice, url: url.trim(), simulation });
      setRapport(r);
      if (r.enregistree) toast.success(`Version ${r.version.numero ?? ''} du PTBA ${exercice} enregistrée (${r.statut === 'SOUMIS' ? 'soumise au Directeur Général' : 'brouillon'}).`);
      else if (r.erreurs.length) toast.error(`${r.erreurs.length} erreur(s) : aucune version enregistrée.`);
      else if (!r.simulation) toast.info(r.avertissements[0]?.message ?? 'Version déjà enregistrée.');
      else toast.info('Simulation terminée, aucune écriture : vérifiez le compte rendu puis enregistrez la version.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Import impossible.');
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Importer une version du PTBA</DialogTitle>
          <DialogDescription>
            Onglet « Export 360 » du classeur PTBA (fichier Excel ou CSV, ou lien Google Sheets de l’onglet). Contrôle complet puis enregistrement d’une version : rien n’est publié dans le suivi budgétaire avant l’approbation du Directeur Général.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="ptba-exercice">Exercice</Label>
              <Input id="ptba-exercice" type="number" min={2020} max={2100} value={exercice} onChange={(e) => (setExercice(Number(e.target.value)), setRapport(null))} />
            </div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={mode === 'fichier' ? 'default' : 'outline'} onClick={() => (setMode('fichier'), setRapport(null))}>
                  Fichier Excel ou CSV
                </Button>
                <Button type="button" size="sm" variant={mode === 'lien' ? 'default' : 'outline'} onClick={() => (setMode('lien'), setRapport(null))}>
                  Lien Google Sheets
                </Button>
              </div>
            </div>
          </div>
          {mode === 'fichier' ? (
            <div className="space-y-1.5">
              <Label htmlFor="ptba-fichier">Classeur (.xlsx) ou export CSV de l’onglet Export 360</Label>
              <Input id="ptba-fichier" type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" onChange={(e) => (setFichier(e.target.files?.[0] ?? null), setRapport(null))} />
              <p className="text-xs text-muted-foreground">Le classeur doit avoir été enregistré après recalcul (Excel ou Google Sheets) : les valeurs des formules sont lues, pas les formules.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="ptba-url">Lien de l’onglet Export 360</Label>
              <Input id="ptba-url" type="url" placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=…" value={url} onChange={(e) => (setUrl(e.target.value), setRapport(null))} />
              <p className="text-xs text-muted-foreground">Dans Google Sheets : Partager › « Toute personne disposant du lien » (lecteur), ouvrir l’onglet Export 360 et copier le lien de la barre d’adresse (il contient le gid de l’onglet).</p>
            </div>
          )}
          {rapport && (
            <div className="space-y-2 rounded-md border bg-muted/30 p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant={rapport.enregistree ? 'default' : rapport.erreurs.length ? 'destructive' : 'secondary'}>{rapport.enregistree ? 'Version enregistrée' : rapport.erreurs.length ? 'Refusé' : rapport.simulation ? 'Simulation' : 'Déjà enregistrée'}</Badge>
                <span className="truncate text-muted-foreground">{rapport.origine}</span>
              </div>
              <RapportPtbaVue rapport={rapport} compact />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button variant="outline" disabled={!pret || enCours} onClick={() => lancer(true)}>
            Simuler
          </Button>
          <Button disabled={!peutEnregistrer || enCours} onClick={() => lancer(false)} title={peutEnregistrer ? undefined : 'Lancez d’abord une simulation sans erreur'}>
            <Upload className="size-4" /> Enregistrer la version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailVersion({ id, onClose, peutDecider }: { id: string; onClose: () => void; peutDecider: boolean }) {
  const { data, isLoading, error } = useVersionPtba(id);
  const actions = useActionsPtba();
  const [approbation, setApprobation] = useState(false);
  const [rejet, setRejet] = useState(false);
  const decider = async (approuver: boolean, motif: string) => {
    try {
      if (approuver) await actions.approuver.mutateAsync({ id, motif: motif || undefined });
      else await actions.rejeter.mutateAsync({ id, motif });
      toast.success(approuver ? 'Version approuvée : sous-activités, lignes de dépense et provisions publiées dans le suivi budgétaire.' : 'Version rejetée, le Directeur financier est informé.');
      setApprobation(false);
      setRejet(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Décision impossible.');
    }
  };
  if (isLoading && !data) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const v: DetailVersionPtba = data;
  const decidable = peutDecider && (v.statut === 'SOUMIS' || v.statut === 'BROUILLON');
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="flex flex-wrap items-center gap-2">
              Version {v.numero} <StatutVersionBadge statut={v.statut} />
              {v.approbationClasseur && v.statut === 'SOUMIS' && <Badge variant="outline">Classeur : Approuvé</Badge>}
            </CardTitle>
            <CardDescription>
              Importée le {dateHeureFr(v.dateImport)} par {v.importeParNom} · {v.sousActivites} sous-activité(s), {v.nombreLignes} ligne(s) de dépense · {montant(v.budget)}
              {v.dateDecision && ` · ${v.statut === 'REJETE' ? 'rejetée' : 'approuvée'} le ${dateHeureFr(v.dateDecision)} par ${v.decideParNom}`}
              {v.motif && ` · ${v.motif}`}
            </CardDescription>
            {v.source && <div className="mt-1 truncate text-xs text-muted-foreground" title={v.source}>{v.source}</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            {decidable && (
              <>
                <Button size="sm" variant="outline" onClick={() => setRejet(true)}>
                  <XCircle className="size-4" /> Rejeter
                </Button>
                <Button size="sm" disabled={v.statut !== 'SOUMIS' || !v.approbationClasseur} title={v.statut !== 'SOUMIS' ? 'Seule une version soumise peut être approuvée' : !v.approbationClasseur ? 'Le classeur ne marque pas cette version « Approuvé »' : undefined} onClick={() => setApprobation(true)}>
                  <CheckCircle2 className="size-4" /> Approuver
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={onClose}>
              Fermer
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <RapportPtbaVue rapport={v.rapport} />
      </CardContent>
      <ConfirmationDialog
        titre={`Approuver la version ${v.numero}`}
        description="L’approbation publie les sous-activités, leurs lignes de dépense et les provisions dans le suivi budgétaire (budgets initiaux et plans mensuels figés, virements conservés). Les sous-activités absentes sont désactivées avec leur exécution ; la version approuvée précédente est remplacée. Les directeurs concernés sont notifiés."
        libelle="Approuver et publier"
        onConfirmer={(texte) => decider(true, texte)}
        chargement={actions.approuver.isPending}
        ouvert={approbation}
        onOpenChange={setApprobation}
        champ={{ label: 'Commentaire (facultatif)', placeholder: 'Référence de la décision, observations…' }}
      />
      <ConfirmationDialog
        titre={`Rejeter la version ${v.numero}`}
        description="La version est rejetée et le Directeur financier en est informé avec le motif ; une version corrigée pourra être importée."
        libelle="Rejeter"
        onConfirmer={(texte) => (texte.length < 3 ? Promise.resolve(toast.error('Indiquez le motif du rejet.')) : decider(false, texte))}
        chargement={actions.rejeter.isPending}
        ouvert={rejet}
        onOpenChange={setRejet}
        champ={{ label: 'Motif du rejet', placeholder: 'Ce qui doit être corrigé dans le classeur…' }}
      />
    </Card>
  );
}

/** Suivi budgétaire › Versions du PTBA : liste des versions importées, rapport, approbation ou rejet par le DG. */
export function BudgetVersions() {
  const session = useSession();
  const u = session!.utilisateur;
  const peutImporter = u.profils.some((p) => PROFILS_IMPORT.includes(p));
  const peutLire = u.profils.some((p) => PROFILS_LECTURE.includes(p));
  const peutDecider = u.profils.includes('DIRECTEUR_GENERAL');
  const [params, setParams] = useSearchParams();
  const [exercice, setExercice] = useState(Number(params.get('exercice')) || new Date().getFullYear() + 1);
  const [importOuvert, setImportOuvert] = useState(false);
  const [selection, setSelection] = useState<string | null>(params.get('version'));
  const versions = useVersionsPtba(exercice, peutLire);
  const an = new Date().getFullYear();
  const choisirExercice = (e: number) => {
    setExercice(e);
    setSelection(null);
    const p = new URLSearchParams(params);
    p.set('exercice', String(e));
    p.delete('version');
    setParams(p, { replace: true });
  };
  const choisirVersion = (id: string | null) => {
    setSelection(id);
    const p = new URLSearchParams(params);
    if (id) p.set('version', id);
    else p.delete('version');
    setParams(p, { replace: true });
  };
  if (!peutLire) return <ErreurBloc erreur={new Error('Écran réservé à la Direction financière, au contrôle de gestion, à la Direction Générale et à l’administrateur.')} />;
  const liste: VersionPtba[] = versions.data ?? [];
  const approuvee = liste.find((v) => v.statut === 'APPROUVE') ?? null;
  return (
    <div className="space-y-5">
      <PageHeader
        retour={
          <Link to="/budget" className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-3.5" /> Suivi budgétaire
          </Link>
        }
        titre={`Versions du PTBA ${exercice}`}
        sousTitre={
          approuvee ? (
            <span>
              Version approuvée en service : <span className="font-medium text-foreground">{approuvee.numero}</span> ({montant(approuvee.budget)}), approuvée le {dateFr(approuvee.dateDecision)} par {approuvee.decideParNom}
            </span>
          ) : (
            'Aucune version approuvée pour cet exercice : le suivi budgétaire ne tient compte d’aucun budget du PTBA.'
          )
        }
        actions={
          <>
            <div className="flex items-center gap-2">
              <Label htmlFor="ptba-ex" className="text-xs text-muted-foreground">
                Exercice
              </Label>
              <Input id="ptba-ex" type="number" className="h-9 w-24" min={2020} max={2100} value={exercice} onChange={(e) => choisirExercice(Number(e.target.value) || an + 1)} />
            </div>
            {peutImporter && (
              <Button onClick={() => setImportOuvert(true)}>
                <Upload className="size-4" /> Importer une version
              </Button>
            )}
            <Button variant="outline" asChild>
              <a href="/aide/specifications-plan-operationnel.html#4-import-versionne-du-plan-et-du-budget" target="_blank" rel="noreferrer">
                <FileSpreadsheet className="size-4" /> Mode d’emploi
              </a>
            </Button>
          </>
        }
      />
      {versions.isLoading && !versions.data ? (
        <Chargement />
      ) : versions.error ? (
        <ErreurBloc erreur={versions.error} />
      ) : !liste.length ? (
        <Vide message={`Aucune version du PTBA ${exercice} importée. ${peutImporter ? 'Importez l’onglet « Export 360 » du classeur pour commencer.' : ''}`} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Versions importées</CardTitle>
            <CardDescription>Une seule version approuvée par exercice ; l’approbation d’une nouvelle version remplace la précédente. Les versions brouillon et soumises n’alimentent jamais le disponible.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Importée</TableHead>
                  <TableHead className="text-right">Sous-activités</TableHead>
                  <TableHead className="text-right">Lignes</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Écart / approuvée</TableHead>
                  <TableHead>Décision</TableHead>
                  <TableHead className="text-right">Contrôles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {liste.map((v) => (
                  <TableRow key={v.id} className={cn('cursor-pointer', selection === v.id && 'bg-muted/50')} onClick={() => choisirVersion(selection === v.id ? null : v.id)}>
                    <TableCell className="font-medium">
                      {v.numero}
                      {v.approbationClasseur && v.statut === 'SOUMIS' && <div className="text-[11px] text-muted-foreground">classeur : Approuvé</div>}
                    </TableCell>
                    <TableCell>
                      <StatutVersionBadge statut={v.statut} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {dateHeureFr(v.dateImport)}
                      <div className="text-xs text-muted-foreground">{v.importeParNom}</div>
                    </TableCell>
                    <TableCell className="num text-right">{v.sousActivites}</TableCell>
                    <TableCell className="num text-right">{v.lignes}</TableCell>
                    <TableCell className="num text-right">{nombre(v.budget)}</TableCell>
                    <TableCell className="num text-right">{v.ecartApprouve === null ? '—' : `${v.ecartApprouve > 0 ? '+' : ''}${nombre(v.ecartApprouve)}`}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      {v.dateDecision ? (
                        <>
                          {dateFr(v.dateDecision)}
                          <div className="text-muted-foreground">{v.decideParNom}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="num text-right text-xs">
                      {v.erreurs > 0 && <span className="text-red-700">{v.erreurs} erreur(s)</span>}
                      {v.erreurs > 0 && v.avertissements > 0 && ' · '}
                      {v.avertissements > 0 && <span className="text-amber-700">{v.avertissements} avert.</span>}
                      {!v.erreurs && !v.avertissements && 'OK'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {selection && <DetailVersion id={selection} onClose={() => choisirVersion(null)} peutDecider={peutDecider} />}
      {peutImporter && <DialogueImportPtba ouvert={importOuvert} onOpenChange={setImportOuvert} exercice={exercice} />}
    </div>
  );
}
